import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  pageTitleClass,
  pageSubtitleClass,
  cardClass,
  cardBodyClass,
  btnSecondaryClass,
  btnDangerClass,
  alertErrorClass,
  badgeClass,
  segmentedGroupClass,
  segmentedButtonClass,
  spinnerClass,
} from "../../dashboardStyles";
import { CancelMyBookingAction } from "../../../../store/action";
import { useMyBookings } from "./useMyBookings";
import EditBookingModal from "./EditBookingModal";
import ConfirmDialog from "../../ConfirmDialog";
import {
  formatDateTime,
  statusBadgeClass,
  daysUntil,
} from "./bookingPresentation";
import { isWallClockPast } from "../../../../utils/dates";
import { notify, notifySaveError } from "../../../../utils/notify";

/**
 * A single appointment, as its customer reads it: who they are seeing, when,
 * and how to reach them. No resource/service ids — those are org-side plumbing.
 *
 * Change/cancel appear only while the booking is still changeable. The same rule
 * is enforced by the API; this just avoids offering a button that would 409.
 */
const BookingCard = ({ booking, locale, t, onEdit, onCancel, busy }) => {
  const days = daysUntil(booking.bookingDateTime);
  const isSoon = days >= 0 && days <= 2;

  const status = booking.status?.toLowerCase();
  const canChange =
    status !== "cancelled" &&
    status !== "completed" &&
    !isWallClockPast(booking.bookingDateTime);

  return (
    <div className={cardClass}>
      <div className={cardBodyClass}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h3 className="m-0 text-base font-semibold text-ink-900">
              {booking.serviceName}
            </h3>
            <p className="mt-1 mb-0 text-fluid-sm text-ink-500">
              <i className="bi bi-shop mr-1.5" aria-hidden="true" />
              {booking.organizationName}
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {isSoon && (
              <span className={`${badgeClass} bg-brand-600 text-white`}>
                {days === 0
                  ? t("Dashboard.MyBookings.today")
                  : days === 1
                    ? t("Dashboard.MyBookings.tomorrow")
                    : t("Dashboard.MyBookings.inDays", { count: days })}
              </span>
            )}
            <span
              className={`${badgeClass} ${statusBadgeClass(booking.status)}`}
            >
              {t(`Dashboard.MyBookings.status.${booking.status?.toLowerCase()}`, {
                defaultValue: booking.status,
              })}
            </span>
          </div>
        </div>

        <dl className="mt-4 grid gap-3 border-t border-ink-100 pt-4 [grid-template-columns:repeat(auto-fit,minmax(min(200px,100%),1fr))]">
          {[
            [
              "bi-calendar-event",
              t("Dashboard.MyBookings.when"),
              formatDateTime(booking.bookingDateTime, locale),
            ],
            [
              "bi-clock",
              t("Dashboard.MyBookings.duration"),
              t("Dashboard.MyBookings.minutes", { count: booking.duration }),
            ],
            ["bi-person", t("Dashboard.MyBookings.with"), booking.resourceName],
            [
              "bi-hash",
              t("Dashboard.MyBookings.confirmationCode"),
              booking.confirmationCode,
            ],
            booking.organizationAddress && [
              "bi-geo-alt",
              t("Dashboard.MyBookings.where"),
              booking.organizationAddress,
            ],
            booking.organizationPhone && [
              "bi-telephone",
              t("Dashboard.MyBookings.phone"),
              booking.organizationPhone,
            ],
          ]
            .filter(Boolean)
            .map(([icon, label, value]) => (
              <div key={label}>
                <dt className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-ink-500">
                  <i className={`bi ${icon}`} aria-hidden="true" />
                  {label}
                </dt>
                <dd className="m-0 text-fluid-sm break-words text-ink-900">
                  {value || "-"}
                </dd>
              </div>
            ))}
        </dl>

        {booking.notes && (
          <p className="mt-3 mb-0 rounded-lg bg-ink-50 p-3 text-fluid-sm text-ink-600">
            <i className="bi bi-sticky mr-1.5" aria-hidden="true" />
            {booking.notes}
          </p>
        )}

        {canChange && (
          <div className="mt-4 flex flex-col gap-2 border-t border-ink-100 pt-4 sm:flex-row sm:justify-end">
            <button
              type="button"
              className={btnSecondaryClass}
              onClick={() => onEdit(booking)}
              disabled={busy}
            >
              <i className="bi bi-pencil" aria-hidden="true" />
              {t("Dashboard.MyBookings.actions.change")}
            </button>
            <button
              type="button"
              className={btnDangerClass}
              onClick={() => onCancel(booking)}
              disabled={busy}
            >
              <i className="bi bi-x-circle" aria-hidden="true" />
              {t("Dashboard.MyBookings.actions.cancel")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const MyBookings = () => {
  const { t, i18n } = useTranslation();
  const { loading, error, upcoming, past, reload } = useMyBookings();
  const [tab, setTab] = useState("upcoming");
  const [editing, setEditing] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [busy, setBusy] = useState(false);

  const shown = tab === "upcoming" ? upcoming : past;

  const closeCancelDialog = useCallback(() => setCancelling(null), []);

  const handleCancel = async () => {
    const booking = cancelling;
    setCancelling(null);
    try {
      setBusy(true);
      await CancelMyBookingAction(booking.id);
      await reload();
      notify.success(t("Dashboard.MyBookings.actions.cancelled"));
    } catch (err) {
      notifySaveError(err, t("Dashboard.MyBookings.errors.cancelFailed"));
    } finally {
      setBusy(false);
    }
  };

  // Refetch rather than patch the row in place: a reschedule can also flip the
  // status back to Pending, and the upcoming/past split may have changed.
  const handleSaved = async () => {
    setEditing(null);
    notify.success(t("Dashboard.MyBookings.actions.saved"));
    await reload();
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div
          role="status"
          aria-label={t("Dashboard.MyBookings.loading")}
          className={spinnerClass}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className={pageTitleClass}>{t("Dashboard.MyBookings.title")}</h1>
        <p className={pageSubtitleClass}>{t("Dashboard.MyBookings.subtitle")}</p>
      </div>

      {error && (
        <div className={alertErrorClass} role="alert">
          <i className="bi bi-exclamation-circle mt-0.5 shrink-0" />
          {t("Dashboard.MyBookings.errors.loadFailed")}
        </div>
      )}

      <div className={`${segmentedGroupClass} mb-5 max-w-sm`}>
        {[
          ["upcoming", t("Dashboard.MyBookings.tabs.upcoming"), upcoming.length],
          ["past", t("Dashboard.MyBookings.tabs.past"), past.length],
        ].map(([id, label, count]) => (
          <button
            key={id}
            type="button"
            aria-pressed={tab === id}
            onClick={() => setTab(id)}
            className={segmentedButtonClass(tab === id)}
          >
            {label} ({count})
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <div className={cardClass}>
          <div className={`${cardBodyClass} py-12 text-center`}>
            <i className="bi bi-calendar-x mb-3 block text-4xl text-ink-300" />
            <p className="m-0 text-fluid-sm text-ink-500">
              {tab === "upcoming"
                ? t("Dashboard.MyBookings.empty.upcoming")
                : t("Dashboard.MyBookings.empty.past")}
            </p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {shown.map((booking) => (
            <BookingCard
              key={booking.id}
              booking={booking}
              locale={i18n.language}
              t={t}
              busy={busy}
              onEdit={setEditing}
              onCancel={setCancelling}
            />
          ))}
        </div>
      )}

      {editing && (
        <EditBookingModal
          booking={editing}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}

      {cancelling && (
        <ConfirmDialog
          message={t("Dashboard.MyBookings.actions.confirmCancel")}
          confirmLabel={t("Dashboard.MyBookings.actions.confirmYes")}
          cancelLabel={t("Dashboard.MyBookings.actions.confirmNo")}
          onConfirm={handleCancel}
          onCancel={closeCancelDialog}
        />
      )}
    </div>
  );
};

export default MyBookings;
